import classNames from 'classnames';
import { observer } from 'mobx-react';
import { createContext, use, useMemo } from 'react';
import { Button as ReactAriaButton } from 'react-aria-components/Button';
import {
  CheckboxButton as ReactAriaCheckboxButton,
  CheckboxField as ReactAriaCheckboxField,
  CheckboxFieldProps as ReactAriaCheckboxFieldProps,
} from 'react-aria-components/Checkbox';
import { composeRenderProps } from 'react-aria-components/composeRenderProps';
import { Group as ReactAriaGroup } from 'react-aria-components/Group';
import {
  Cell as ReactAriaCell,
  Collection as ReactAriaCollection,
  Column as ReactAriaColumn,
  ResizableTableContainer,
  Row as ReactAriaRow,
  SortDirection,
  Table as ReactAriaTable,
  TableBody as ReactAriaTableBody,
  TableHeader as ReactAriaTableHeader,
  TableLoadMoreItem as ReactAriaTableLoadMoreItem,
  type CellProps as ReactAriaTableCellProps,
  type CollectionProps as ReactAriaCollectionProps,
  type ColumnProps as ReactAriaColumnProps,
  type RowProps as ReactAriaRowProps,
  type TableBodyProps as ReactAriaTableBodyProps,
  type TableHeaderProps as ReactAriaTableHeaderProps,
  type TableLoadMoreItemProps as ReactAriaTableLoadMoreItemProps,
  type TableProps as ReactAriaTableProps,
} from 'react-aria-components/Table';
import { Virtualizer } from 'react-aria-components/Virtualizer';

import Icon from '../../../app/shared/components/Icon/Icon';
import { Tooltip, TooltipTrigger } from '../../../app/shared/components/Tooltip/Tooltip';
import { createDataGridLayout } from './createDataGridLayout';
import css from './DataGrid.css';

/**
 * Selection is driven by the caller rather than react-aria's built-in selection, so that
 * a row press is always free to trigger its action (e.g. expand) and selection only ever
 * changes through these checkboxes.
 *
 * The state is read through functions, called inside each checkbox's own observer render: a
 * change repaints only the checkboxes it affects instead of invalidating every cached row. Pass
 * an object that keeps its identity between renders — every row reads it from context.
 */
export interface DataGridSelection {
  isAllIndeterminate: () => boolean;
  isAllSelected: () => boolean;
  isRowIndeterminate: (id: string) => boolean;
  isRowSelected: (id: string) => boolean;
  onToggleAll: (isSelected: boolean) => void;
  onToggleRow: (id: string, isSelected: boolean) => void;
  rowLabel: string;
  selectAllLabel: string;
}

export interface DataGridExpansion {
  collapseLabel: string;
  columnLabel: string;
  expandLabel: string;
}

interface DataGridContextValue {
  expansion: DataGridExpansion | null;
  selection: DataGridSelection | null;
}

const DataGridContext = createContext<DataGridContextValue>({ expansion: null, selection: null });

interface DataGridProps
  extends Pick<
    ReactAriaTableProps,
    'aria-label' | 'children' | 'expandedKeys' | 'onExpandedChange' | 'onSortChange' | 'sortDescriptor'
  > {
  className?: string;
  /**
   * The row height, in px, the virtualizer assumes for a row it has not measured yet. Rows are
   * measured once rendered, so this only sizes the scrollbar — the closer it is, the less it jumps.
   */
  estimatedRowHeight: number;
  expansion?: DataGridExpansion;
  /** The height, in px, of the `DataGridLoadMore` row while it is loading; the virtualizer does not measure it. */
  loaderHeight?: number;
  /** When provided, a selection column of checkboxes is rendered and driven by these callbacks. */
  selection?: DataGridSelection;
}

/**
 * Virtualized: only the rows in view (plus the focused one) are mounted. React-aria re-renders every
 * mounted row whenever the table state changes — focus moving to another row, expanding one — so the
 * cost of an interaction follows the number of mounted rows, not the number loaded.
 */
export const DataGrid = ({
  className,
  estimatedRowHeight,
  expansion,
  loaderHeight,
  selection,
  ...props
}: DataGridProps) => {
  // Every row and cell reads the context, so a new value would re-render all of them.
  const context = useMemo(
    () => ({ expansion: expansion ?? null, selection: selection ?? null }),
    [expansion, selection],
  );
  // One layout per grid: it holds that grid's layout state.
  const layout = useMemo(createDataGridLayout, []);
  const layoutOptions = useMemo(() => ({ estimatedRowHeight, loaderHeight }), [estimatedRowHeight, loaderHeight]);

  return (
    <DataGridContext value={context}>
      <ResizableTableContainer className={classNames(css.root, className)}>
        <Virtualizer layout={layout} layoutOptions={layoutOptions}>
          <ReactAriaTable className={css.table} treeColumn="expand" {...props} />
        </Virtualizer>
      </ResizableTableContainer>
    </DataGridContext>
  );
};

interface DataGridHeaderProps<T> extends Pick<ReactAriaTableHeaderProps<T>, 'children' | 'columns' | 'dependencies'> {
  children: React.ReactNode;
}

export const DataGridHeader = <T,>({ columns, children, ...props }: DataGridHeaderProps<T>) => {
  const { expansion, selection } = use(DataGridContext);

  return (
    <ReactAriaTableHeader className={css.header} {...props}>
      <ReactAriaColumn id="expand" width={32} minWidth={32}>
        {expansion && <span className="visuallyHidden">{expansion.columnLabel}</span>}
      </ReactAriaColumn>
      {/* The select column takes its accessible name from the checkbox it contains. */}
      {selection && (
        <ReactAriaColumn className={css.columnSelect} width={32} minWidth={32}>
          <SelectionCheckbox
            aria-label={selection.selectAllLabel}
            isSelected={selection.isAllSelected}
            isIndeterminate={selection.isAllIndeterminate}
            onChange={selection.onToggleAll}
          />
        </ReactAriaColumn>
      )}
      {children}
    </ReactAriaTableHeader>
  );
};

interface RowProps<T> extends Pick<ReactAriaRowProps<T>, 'columns' | 'id' | 'isDisabled' | 'onAction'> {
  children: React.ReactNode;
  className?: string;
}

export const Row = <T,>({ children, className, columns, id, isDisabled, ...props }: RowProps<T>) => {
  const { selection } = use(DataGridContext);

  return (
    <ReactAriaRow
      id={id}
      className={classNames(css.row, className)}
      isDisabled={isDisabled}
      disabledBehavior="all"
      {...props}
    >
      <Cell />
      {selection && (
        <Cell>
          <SelectionCheckbox
            aria-label={selection.rowLabel}
            isDisabled={isDisabled}
            isSelected={() => selection.isRowSelected(String(id))}
            isIndeterminate={() => selection.isRowIndeterminate(String(id))}
            onChange={(isSelected) => selection.onToggleRow(String(id), isSelected)}
          />
        </Cell>
      )}
      {children}
    </ReactAriaRow>
  );
};

export interface ColumnTooltip {
  label: string;
  content: string;
}

interface ColumnProps
  extends Pick<ReactAriaColumnProps, 'allowsSorting' | 'id' | 'isRowHeader' | 'width' | 'minWidth'> {
  children: React.ReactNode;
  className?: string;
  tooltip?: ColumnTooltip;
}

export const Column = ({ className, tooltip, ...props }: ColumnProps) => (
  <ReactAriaColumn className={classNames(css.column, className)} {...props}>
    {composeRenderProps(props.children, (children, { allowsSorting, sortDirection }) => (
      <ReactAriaGroup role="presentation" className={css.columnHeader} tabIndex={-1}>
        <span className={css.columnName}>{children}</span>
        {tooltip && (
          <TooltipTrigger>
            <ReactAriaButton className={css.tooltipTrigger} aria-label={tooltip.label}>
              <Icon name="info" />
            </ReactAriaButton>
            <Tooltip>{tooltip.content}</Tooltip>
          </TooltipTrigger>
        )}
        {allowsSorting && <SortingIcon direction={sortDirection} />}
      </ReactAriaGroup>
    ))}
  </ReactAriaColumn>
);

interface SortingIconProps {
  direction: SortDirection;
}

const SortingIcon = ({ direction }: SortingIconProps) => {
  switch (direction) {
    case 'ascending':
      return <Icon className={classNames(css.sortingIcon, css.active)} name="angleUp" />;
    case 'descending':
      return <Icon className={classNames(css.sortingIcon, css.active)} name="angleDown" />;
    default:
      return <Icon className={css.sortingIcon} name="sort" />;
  }
};

interface CellProps extends Pick<ReactAriaTableCellProps, 'children'> {
  className?: string;
}

export const Cell = ({ className, ...props }: CellProps) => {
  const { expansion } = use(DataGridContext);

  return (
    <ReactAriaCell {...props} className={classNames(css.cell, className)}>
      {composeRenderProps(props.children, (children, { hasChildItems, isExpanded, isTreeColumn }) => (
        <>
          {isTreeColumn && hasChildItems && (
            <ReactAriaButton
              slot="chevron"
              className={css.expandButton}
              aria-label={isExpanded ? expansion?.collapseLabel : expansion?.expandLabel}
            >
              <Icon className={css.icon} name="angleDown" />
            </ReactAriaButton>
          )}
          {children}
        </>
      ))}
    </ReactAriaCell>
  );
};

type DataGridBodyProps<T> = Pick<
  ReactAriaTableBodyProps<T>,
  'children' | 'dependencies' | 'items' | 'renderEmptyState'
>;

export const DataGridBody = <T,>(props: DataGridBodyProps<T>) => {
  return <ReactAriaTableBody {...props} />;
};

type CollectionProps<T> = Pick<ReactAriaCollectionProps<T>, 'children' | 'dependencies' | 'items'>;

export const Collection = <T,>(props: CollectionProps<T>) => {
  return <ReactAriaCollection {...props} />;
};

type DataGridLoadMoreProps = Pick<
  ReactAriaTableLoadMoreItemProps,
  'children' | 'isLoading' | 'onLoadMore' | 'scrollOffset'
>;

/**
 * A sentinel placed at the end of the body: react-aria observes it against the grid's own
 * scrollport and calls `onLoadMore` once it comes into view, then renders `children` as a
 * full-width row while `isLoading`. The callback can fire more than once for the same page,
 * so the caller has to guard against re-entry.
 *
 * It counts as a row of the collection, so render it only when there are rows — while the
 * body is empty it would suppress the body's empty state.
 */
export const DataGridLoadMore = (props: DataGridLoadMoreProps) => {
  return <ReactAriaTableLoadMoreItem {...props} />;
};

interface SelectionCheckboxProps extends Pick<ReactAriaCheckboxFieldProps, 'aria-label' | 'isDisabled' | 'onChange'> {
  isIndeterminate: () => boolean;
  isSelected: () => boolean;
}

/** An observer, so the state it reads in `isSelected` / `isIndeterminate` repaints this checkbox alone. */
const SelectionCheckbox = observer(({ isIndeterminate, isSelected, ...props }: SelectionCheckboxProps) => (
  <ReactAriaCheckboxField
    // The React Aria Table provides a slotted CheckboxFieldContext for its own selection checkbox.
    // The custom checkbox is fully controlled by SelectionState, so slot={null} opts out of that context.
    slot={null}
    className={css.checkbox}
    isIndeterminate={isIndeterminate()}
    isSelected={isSelected()}
    {...props}
  >
    <ReactAriaCheckboxButton>
      {(renderProps) => (
        <span className={css.indicator}>
          {(renderProps.isSelected || renderProps.isIndeterminate) && (
            <Icon name={renderProps.isIndeterminate ? 'minus' : 'check'} className={css.icon} />
          )}
        </span>
      )}
    </ReactAriaCheckboxButton>
  </ReactAriaCheckboxField>
));
